import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  UserGroupIcon,
  ClipboardDocumentListIcon,
  PlusIcon,
  UserPlusIcon,
  UserMinusIcon,
  ChartBarIcon,
  FolderIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ArrowTrendingUpIcon,
  Cog6ToothIcon,
  PencilIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import Layout from '../components/layout/Layout';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { useAuth } from '../contexts/AuthContext';
import { usersAPI, teamsAPI, tasksAPI } from '../services/api';
import { Task, Team, User, Project } from '../types';
import TaskModal from '../components/admin/TaskModal';
import UserTaskModal from '../components/user/UserTaskModal';
import ConfirmDialog from '../components/common/ConfirmDialog';
import toast from 'react-hot-toast';

interface ManagedTeam {
  id: string;
  name: string;
  description?: string;
  color: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  managerId: string;
  manager: {
    id: string;
    firstName: string;
    lastName: string;
    username: string;
    email: string;
  };
  _count: {
    members: number;
    tasks: number;
    projects: number;
  };
  members: Array<{
    id: string;
    user: User;
    joinedAt: string;
  }>;
  projects: Array<{
    project: Project;
  }>;
  tasks: Task[];
  performance: {
    totalTasks: number;
    completedTasks: number;
    overdueTasks: number;
    completionRate: number;
  };
}

interface TeamMember extends User {
  joinedAt: string;
  taskCount: number;
  completedTasks: number;
  completionRate: number;
}

const Management: React.FC = () => {
  const { user } = useAuth();
  const [managedTeams, setManagedTeams] = useState<ManagedTeam[]>([]);
  const [selectedTeam, setSelectedTeam] = useState<ManagedTeam | null>(null);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [teamProjects, setTeamProjects] = useState<Project[]>([]);
  const [teamTasks, setTeamTasks] = useState<Task[]>([]);
  const [availableUsers, setAvailableUsers] = useState<User[]>([]);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [isCreateTaskModalOpen, setIsCreateTaskModalOpen] = useState(false);
  const [isUserTaskModalOpen, setIsUserTaskModalOpen] = useState(false);
  const [isAddMemberModalOpen, setIsAddMemberModalOpen] = useState(false);
  const [isRemoveMemberConfirmOpen, setIsRemoveMemberConfirmOpen] = useState(false);
  const [isTaskAssignmentModalOpen, setIsTaskAssignmentModalOpen] = useState(false);
  const [memberToRemove, setMemberToRemove] = useState<TeamMember | null>(null);
  const [selectedMembers, setSelectedMembers] = useState<string[]>([]);
  const [taskToManage, setTaskToManage] = useState<Task | null>(null);
  const [taskAssignees, setTaskAssignees] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingTeamData, setIsLoadingTeamData] = useState(false);
  const [activeTab, setActiveTab] = useState<'overview' | 'tasks' | 'members' | 'projects'>('overview');
  const [taskToDelete, setTaskToDelete] = useState<Task | null>(null);
  const [isDeleteTaskDialogOpen, setIsDeleteTaskDialogOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [isEditTaskModalOpen, setIsEditTaskModalOpen] = useState(false);

  useEffect(() => {
    if (user?.role === 'MANAGER') {
      loadManagedTeams();
      loadAvailableUsers();
    }
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-refresh team data every 30 seconds to show real-time updates
  useEffect(() => {
    if (!selectedTeam) return;

    const interval = setInterval(() => {
      loadTeamDetails(selectedTeam);
    }, 30000); // Refresh every 30 seconds

    return () => clearInterval(interval);
  }, [selectedTeam]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadManagedTeams = async () => {
    if (!user) return;

    try {
      setIsLoading(true);
      // Get teams where current user is the manager
      const teams = await teamsAPI.getAll();
      const managedTeams = teams.filter((team: any) => team.managerId === user.id);
      
      // Load detailed data for each managed team
      const teamsWithDetails = await Promise.all(
        managedTeams.map(async (team: any) => {
          try {
            const [performance, tasks, projects] = await Promise.all([
              teamsAPI.getTeamPerformance(team.id),
              teamsAPI.getTeamTasks(team.id),
              teamsAPI.getTeamProjects ? teamsAPI.getTeamProjects(team.id) : [],
            ]);

            return {
              ...team,
              performance,
              tasks: tasks || [],
              projects: projects || [],
            };
          } catch (error) {
            console.error(`Error loading data for team ${team.id}:`, error);
            return {
              ...team,
              performance: { totalTasks: 0, completedTasks: 0, overdueTasks: 0, completionRate: 0 },
              tasks: [],
              projects: [],
            };
          }
        })
      );

      setManagedTeams(teamsWithDetails);
      
      if (teamsWithDetails.length > 0) {
        setSelectedTeam(teamsWithDetails[0]);
        loadTeamDetails(teamsWithDetails[0]);
      }
    } catch (error) {
      console.error('Error loading managed teams:', error);
      toast.error('Failed to load managed teams');
    } finally {
      setIsLoading(false);
    }
  };

  const loadAvailableUsers = async () => {
    try {
      const users = await usersAPI.getAll();
      // Filter out admins and current user
      const availableUsers = users.filter((u: User) => 
        u.role === 'USER' && u.id !== user?.id
      );
      setAvailableUsers(availableUsers);
    } catch (error) {
      console.error('Error loading available users:', error);
    }
  };

  const loadTeamDetails = async (team: ManagedTeam) => {
    try {
      setIsLoadingTeamData(true);
      const [teamData, tasks] = await Promise.all([
        teamsAPI.getById(team.id),
        teamsAPI.getTeamTasks(team.id),
      ]);

      // Get team projects from team-project relationships
      const projects = teamData.projects?.map((tp: any) => tp.project) || [];

      // Combine regular tasks with generated tasks from recurring templates
      const allTasks = [...tasks];

      // Calculate member statistics with all tasks
      const membersWithStats = teamData.members?.map((member: any) => {
        const memberTasks = allTasks.filter((task: Task) =>
          task.assignees.some(assignee => assignee.user.id === member.user.id)
        );
        const completedTasks = memberTasks.filter((task: Task) => task.status === 'DONE');
        const completionRate = memberTasks.length > 0 ? (completedTasks.length / memberTasks.length) * 100 : 0;

        return {
          ...member.user,
          joinedAt: member.joinedAt,
          taskCount: memberTasks.length,
          completedTasks: completedTasks.length,
          completionRate: Math.round(completionRate),
        };
      }) || [];

      setTeamMembers(membersWithStats);
      setTeamProjects(projects);
      setTeamTasks(allTasks);
    } catch (error) {
      console.error('Error loading team details:', error);
      toast.error('Failed to load team details');
    } finally {
      setIsLoadingTeamData(false);
    }
  };

  const handleTeamSelect = (team: ManagedTeam) => {
    setSelectedTeam(team);
    loadTeamDetails(team);
    setActiveTab('overview');
  };

  const handleAddMembers = async () => {
    if (!selectedTeam || selectedMembers.length === 0) return;

    try {
      await teamsAPI.addMembers(selectedTeam.id, { userIds: selectedMembers });
      toast.success('Members added successfully');
      setIsAddMemberModalOpen(false);
      setSelectedMembers([]);
      loadTeamDetails(selectedTeam);
      loadManagedTeams();
    } catch (error) {
      console.error('Error adding members:', error);
      toast.error('Failed to add members');
    }
  };

  const handleRemoveMember = async () => {
    if (!selectedTeam || !memberToRemove) return;

    try {
      await teamsAPI.removeMember(selectedTeam.id, memberToRemove.id);
      toast.success('Member removed successfully');
      setIsRemoveMemberConfirmOpen(false);
      setMemberToRemove(null);
      loadTeamDetails(selectedTeam);
      loadManagedTeams();
    } catch (error) {
      console.error('Error removing member:', error);
      toast.error('Failed to remove member');
    }
  };

  const handleCreateTask = async (taskData: any) => {
    try {
      setIsLoading(true);

      if (!taskData.isRecurring) {
        // For regular tasks, ensure creatorId is included
        const taskDataWithCreator = {
          ...taskData,
          creatorId: user?.id || 'cmdd8jvdu0001v1hsfzjmmox6', // Fallback
          teamId: selectedTeam?.id,
        };
        await tasksAPI.create(taskDataWithCreator);
        toast.success('Task created successfully');
      } else {
        // For recurring tasks, the creatorId is already included in recurringTaskData
        await tasksAPI.create(taskData);
        toast.success('Recurring task created successfully');
      }

      setIsCreateTaskModalOpen(false);
      if (selectedTeam) {
        loadTeamDetails(selectedTeam);
        loadManagedTeams();
      }
    } catch (error) {
      console.error('Error creating task:', error);
      toast.error('Failed to create task');
    } finally {
      setIsLoading(false);
    }
  };

  const handleTaskClick = (task: Task) => {
    setSelectedTask(task);
    setIsUserTaskModalOpen(true);
  };

  const handleDeleteTask = (task: Task) => {
    setTaskToDelete(task);
    setIsDeleteTaskDialogOpen(true);
  };

  const confirmDeleteTask = async () => {
    if (!taskToDelete) return;

    try {
      setIsLoading(true);
      await tasksAPI.delete(taskToDelete.id);
      toast.success('Task deleted successfully');
      setIsDeleteTaskDialogOpen(false);
      setTaskToDelete(null);
      if (selectedTeam) {
        loadTeamDetails(selectedTeam);
        loadManagedTeams();
      }
    } catch (error) {
      console.error('Error deleting task:', error);
      toast.error('Failed to delete task');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditTask = (task: Task) => {
    setEditingTask(task);
    setIsEditTaskModalOpen(true);
  };

  const handleUpdateTask = async (taskData: any) => {
    if (!editingTask || !selectedTeam) return;

    try {
      setIsLoading(true);

      // Ensure the task remains assigned to the current team
      const updateData = {
        ...taskData,
        teamId: selectedTeam.id, // Force the task to stay in the current team
      };

      // Remove any fields that shouldn't be changed by managers
      delete updateData.isRecurring;
      delete updateData.recurringTaskData;
      delete updateData.creatorId; // Don't try to update creator

      const updatedTask = await tasksAPI.update(editingTask.id, updateData);

      // Verify the task still belongs to the current team
      if (updatedTask.team?.id === selectedTeam.id) {
        // Update the local state immediately to prevent disappearing
        setTeamTasks(prev => prev.map(t => t.id === editingTask.id ? updatedTask : t));
      } else {
        // Remove the task from local state if it no longer belongs to this team
        setTeamTasks(prev => prev.filter(t => t.id !== editingTask.id));
      }

      toast.success('Task updated successfully');
      setIsEditTaskModalOpen(false);
      setEditingTask(null);

      // Delay the reload slightly to allow the UI to update first
      setTimeout(() => {
        if (selectedTeam) {
          loadTeamDetails(selectedTeam);
          loadManagedTeams();
        }
      }, 100);
    } catch (error) {
      console.error('Error updating task:', error);
      toast.error('Failed to update task');
    } finally {
      setIsLoading(false);
    }
  };

  const handleTaskUpdate = (updatedTask: Task) => {
    // Update the task in the local state immediately
    setTeamTasks(prev => prev.map(t => t.id === updatedTask.id ? updatedTask : t));

    // Update team data to reflect the changes
    if (selectedTeam) {
      setSelectedTeam(prev => {
        if (!prev) return prev;

        // Update the team's task statistics
        const updatedTeamTasks = teamTasks.map(t => t.id === updatedTask.id ? updatedTask : t);
        const completedTasks = updatedTeamTasks.filter(t => t.status === 'DONE').length;
        const totalTasks = updatedTeamTasks.length;
        const completionRate = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;

        return {
          ...prev,
          completionRate,
          taskCount: totalTasks,
          completedTasks
        };
      });

      // Reload team data to get the most up-to-date information
      setTimeout(() => {
        loadTeamDetails(selectedTeam);
        loadManagedTeams();
      }, 500);
    }

    // Show notification for task completion
    if (updatedTask.status === 'DONE') {
      const assigneeNames = updatedTask.assignees.map(a => `${a.user.firstName} ${a.user.lastName}`).join(', ');
      toast.success(`🎉 Task "${updatedTask.title}" completed by ${assigneeNames}!`);
    }
  };

  const handleManageTaskAssignments = (task: Task) => {
    setTaskToManage(task);
    setTaskAssignees(task.assignees.map(assignee => assignee.user.id));
    setIsTaskAssignmentModalOpen(true);
  };

  const handleUpdateTaskAssignments = async () => {
    if (!taskToManage) return;

    try {
      await tasksAPI.update(taskToManage.id, {
        assigneeIds: taskAssignees,
      });
      toast.success('Task assignments updated successfully');
      setIsTaskAssignmentModalOpen(false);
      setTaskToManage(null);
      setTaskAssignees([]);
      if (selectedTeam) {
        loadTeamDetails(selectedTeam);
        loadManagedTeams();
      }
    } catch (error) {
      console.error('Error updating task assignments:', error);
      toast.error('Failed to update task assignments');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'DONE':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'IN_PROGRESS':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'TODO':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'BLOCKED':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'URGENT':
        return 'bg-red-500';
      case 'HIGH':
        return 'bg-orange-500';
      case 'MEDIUM':
        return 'bg-yellow-500';
      case 'LOW':
        return 'bg-green-500';
      default:
        return 'bg-gray-500';
    }
  };

  const isOverdue = (dueDate?: string) => {
    if (!dueDate) return false;
    return new Date(dueDate) < new Date();
  };

  // Check if user is a manager
  if (user?.role !== 'MANAGER') {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-red-500" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">Access Denied</h3>
            <p className="mt-1 text-sm text-gray-500">
              This section is only available to managers.
            </p>
          </div>
        </div>
      </Layout>
    );
  }

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-screen">
          <LoadingSpinner size="lg" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center space-x-3">
              <Cog6ToothIcon className="w-8 h-8 text-primary-600" />
              <span>Team Management</span>
            </h1>
            <p className="text-gray-600 mt-1">
              Manage your teams, assign tasks, and track progress
            </p>
          </div>
        </div>

        {managedTeams.length === 0 ? (
          <div className="text-center py-12">
            <UserGroupIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No teams to manage</h3>
            <p className="mt-1 text-sm text-gray-500">
              You are not currently managing any teams. Contact your administrator to be assigned as a team manager.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Teams List */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                <div className="p-4 border-b border-gray-200">
                  <h2 className="text-lg font-semibold text-gray-900">My Teams ({managedTeams.length})</h2>
                </div>
                <div className="divide-y divide-gray-200">
                  {managedTeams.map((team) => (
                    <motion.div
                      key={team.id}
                      whileHover={{ backgroundColor: '#f9fafb' }}
                      className={`p-4 cursor-pointer transition-colors ${
                        selectedTeam?.id === team.id ? 'bg-primary-50 border-r-2 border-primary-500' : ''
                      }`}
                      onClick={() => handleTeamSelect(team)}
                    >
                      <div className="flex items-start space-x-3">
                        <div
                          className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                          style={{ backgroundColor: team.color }}
                        >
                          <UserGroupIcon className="w-5 h-5 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-sm font-medium text-gray-900 truncate">
                            {team.name}
                          </h3>
                          {team.description && (
                            <p className="text-xs text-gray-500 mt-1 line-clamp-2">
                              {team.description}
                            </p>
                          )}
                          <div className="flex items-center justify-between mt-2">
                            <div className="flex items-center space-x-3 text-xs text-gray-500">
                              <span>{team._count?.members || 0} members</span>
                              <span>{team._count?.tasks || 0} tasks</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>

            {/* Team Management Panel */}
            <div className="lg:col-span-3">
              {selectedTeam ? (
                <div className="space-y-6">
                  {/* Team Header */}
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center space-x-4">
                        <div
                          className="w-16 h-16 rounded-full flex items-center justify-center"
                          style={{ backgroundColor: selectedTeam.color }}
                        >
                          <UserGroupIcon className="w-8 h-8 text-white" />
                        </div>
                        <div>
                          <h2 className="text-2xl font-bold text-gray-900">{selectedTeam.name}</h2>
                          {selectedTeam.description && (
                            <p className="text-gray-600 mt-1">{selectedTeam.description}</p>
                          )}
                          <div className="flex items-center space-x-4 mt-2 text-sm text-gray-500">
                            <span>{teamMembers.length} members</span>
                            <span>•</span>
                            <span>{teamTasks.length} tasks</span>
                            <span>•</span>
                            <span>{teamProjects.length} projects</span>
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => setIsCreateTaskModalOpen(true)}
                        className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                      >
                        <PlusIcon className="w-4 h-4 mr-2" />
                        Assign Task
                      </button>
                    </div>

                    {/* Team Stats */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="bg-blue-50 p-4 rounded-lg">
                        <div className="flex items-center">
                          <ClipboardDocumentListIcon className="w-5 h-5 text-blue-600" />
                          <span className="ml-2 text-sm font-medium text-blue-900">Total Tasks</span>
                        </div>
                        <p className="text-2xl font-bold text-blue-900 mt-1">{selectedTeam.performance?.totalTasks || 0}</p>
                      </div>
                      <div className="bg-green-50 p-4 rounded-lg">
                        <div className="flex items-center">
                          <CheckCircleIcon className="w-5 h-5 text-green-600" />
                          <span className="ml-2 text-sm font-medium text-green-900">Completed</span>
                        </div>
                        <p className="text-2xl font-bold text-green-900 mt-1">{selectedTeam.performance?.completedTasks || 0}</p>
                      </div>
                      <div className="bg-red-50 p-4 rounded-lg">
                        <div className="flex items-center">
                          <ExclamationTriangleIcon className="w-5 h-5 text-red-600" />
                          <span className="ml-2 text-sm font-medium text-red-900">Overdue</span>
                        </div>
                        <p className="text-2xl font-bold text-red-900 mt-1">{selectedTeam.performance?.overdueTasks || 0}</p>
                      </div>
                      <div className="bg-purple-50 p-4 rounded-lg">
                        <div className="flex items-center">
                          <ArrowTrendingUpIcon className="w-5 h-5 text-purple-600" />
                          <span className="ml-2 text-sm font-medium text-purple-900">Progress</span>
                        </div>
                        <p className="text-2xl font-bold text-purple-900 mt-1">
                          {Math.round(selectedTeam.performance?.completionRate || 0)}%
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Tabs */}
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                    <div className="border-b border-gray-200">
                      <nav className="-mb-px flex space-x-8 px-6">
                        {[
                          { id: 'overview', name: 'Overview', icon: ChartBarIcon },
                          { id: 'tasks', name: 'Tasks', icon: ClipboardDocumentListIcon },
                          { id: 'members', name: 'Members', icon: UserGroupIcon },
                          { id: 'projects', name: 'Projects', icon: FolderIcon },
                        ].map((tab) => (
                          <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id as any)}
                            className={`${
                              activeTab === tab.id
                                ? 'border-primary-500 text-primary-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2`}
                          >
                            <tab.icon className="w-4 h-4" />
                            <span>{tab.name}</span>
                          </button>
                        ))}
                      </nav>
                    </div>

                    <div className="p-6">
                      {isLoadingTeamData ? (
                        <div className="flex items-center justify-center py-12">
                          <LoadingSpinner size="lg" />
                        </div>
                      ) : (
                        <>
                          {activeTab === 'overview' && (
                            <div className="space-y-6">
                              {/* Recent Tasks */}
                              <div>
                                <h3 className="text-lg font-medium text-gray-900 mb-4">Recent Tasks</h3>
                                <div className="space-y-3">
                                  {teamTasks.slice(0, 5).map((task) => (
                                    <motion.div
                                      key={task.id}
                                      whileHover={{ scale: 1.01 }}
                                      className="p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                                    >
                                      <div className="flex items-center justify-between">
                                        <div
                                          className="flex items-center space-x-3 flex-1 cursor-pointer"
                                          onClick={() => handleTaskClick(task)}
                                        >
                                          <div className={`w-2 h-2 rounded-full ${getPriorityColor(task.priority)}`} />
                                          <span className={`text-sm font-medium ${task.status === 'DONE' ? 'text-green-700 line-through' : 'text-gray-900'}`}>
                                            {task.title}
                                          </span>
                                          {task.status === 'DONE' && (
                                            <CheckCircleIcon className="w-4 h-4 text-green-500" />
                                          )}
                                          {isOverdue(task.dueDate) && task.status !== 'DONE' && (
                                            <ExclamationTriangleIcon className="w-4 h-4 text-red-500" />
                                          )}
                                        </div>
                                        <div className="flex items-center space-x-2">
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleManageTaskAssignments(task);
                                            }}
                                            className="px-2 py-1 text-xs font-medium text-primary-600 bg-primary-50 border border-primary-200 rounded hover:bg-primary-100"
                                          >
                                            Manage
                                          </button>
                                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${getStatusColor(task.status)}`}>
                                            {task.status.replace('_', ' ')}
                                          </span>
                                          {task.dueDate && (
                                            <span className="text-xs text-gray-500">
                                              {new Date(task.dueDate).toLocaleDateString()}
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    </motion.div>
                                  ))}
                                  {teamTasks.length === 0 && (
                                    <p className="text-sm text-gray-500 text-center py-4">No tasks found</p>
                                  )}
                                </div>
                              </div>
                            </div>
                          )}

                          {activeTab === 'tasks' && (
                            <div className="space-y-4">
                              <div className="flex items-center justify-between">
                                <h3 className="text-lg font-medium text-gray-900">Team Tasks ({teamTasks.length})</h3>
                                <div className="flex items-center space-x-2">
                                  <button
                                    onClick={() => {
                                      if (selectedTeam) {
                                        loadTeamDetails(selectedTeam);
                                        toast.success('Tasks refreshed');
                                      }
                                    }}
                                    className="inline-flex items-center px-2 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                                    title="Refresh tasks"
                                  >
                                    <ArrowTrendingUpIcon className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={() => setIsCreateTaskModalOpen(true)}
                                    className="inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700"
                                  >
                                    <PlusIcon className="w-4 h-4 mr-2" />
                                    Assign Task
                                  </button>
                                </div>
                              </div>
                              <div className="space-y-3">
                                {teamTasks.map((task) => (
                                  <motion.div
                                    key={task.id}
                                    whileHover={{ scale: 1.01 }}
                                    className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                                  >
                                    <div className="flex items-center justify-between">
                                      <div
                                        className="flex items-center space-x-3 flex-1 cursor-pointer"
                                        onClick={() => handleTaskClick(task)}
                                      >
                                        <div className={`w-3 h-3 rounded-full ${getPriorityColor(task.priority)}`} />
                                        <div className="flex-1">
                                          <h4 className="text-sm font-medium text-gray-900">{task.title}</h4>
                                          <p className="text-xs text-gray-500 mt-1">
                                            Assigned to: {task.assignees.map(a => `${a.user.firstName} ${a.user.lastName}`).join(', ') || 'Unassigned'}
                                          </p>
                                          {task.progressPercentage !== undefined && task.progressPercentage > 0 && (
                                            <div className="mt-2">
                                              <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                                                <span>Progress</span>
                                                <span>{task.progressPercentage}%</span>
                                              </div>
                                              <div className="w-full bg-gray-200 rounded-full h-1.5">
                                                <div
                                                  className="bg-blue-600 h-1.5 rounded-full transition-all duration-300"
                                                  style={{ width: `${task.progressPercentage}%` }}
                                                />
                                              </div>
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                      <div className="flex items-center space-x-2">
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleEditTask(task);
                                          }}
                                          className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                                          title="Edit Task"
                                        >
                                          <PencilIcon className="w-4 h-4" />
                                        </button>
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleDeleteTask(task);
                                          }}
                                          className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                                          title="Delete Task"
                                        >
                                          <TrashIcon className="w-4 h-4" />
                                        </button>
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleManageTaskAssignments(task);
                                          }}
                                          className="px-2 py-1 text-xs font-medium text-primary-600 bg-primary-50 border border-primary-200 rounded hover:bg-primary-100"
                                        >
                                          Manage
                                        </button>
                                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${getStatusColor(task.status)}`}>
                                          {task.status.replace('_', ' ')}
                                        </span>
                                        {task.status === 'DONE' && task.updatedAt && (
                                          <span className="text-xs text-green-600 font-medium">
                                            Completed: {new Date(task.updatedAt).toLocaleDateString()}
                                          </span>
                                        )}
                                        {task.status !== 'DONE' && task.dueDate && (
                                          <span className={`text-xs ${isOverdue(task.dueDate) ? 'text-red-600 font-medium' : 'text-gray-500'}`}>
                                            Due: {new Date(task.dueDate).toLocaleDateString()}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  </motion.div>
                                ))}
                                {teamTasks.length === 0 && (
                                  <p className="text-sm text-gray-500 text-center py-8">No tasks assigned yet</p>
                                )}
                              </div>
                            </div>
                          )}

                          {activeTab === 'members' && (
                            <div className="space-y-6">
                              <div className="flex items-center justify-between">
                                <h3 className="text-lg font-medium text-gray-900">Team Members ({teamMembers.length})</h3>
                                <button
                                  onClick={() => setIsAddMemberModalOpen(true)}
                                  className="inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700"
                                >
                                  <UserPlusIcon className="w-4 h-4 mr-2" />
                                  Add Member
                                </button>
                              </div>

                              {/* Team Performance Summary */}
                              {teamMembers.length > 0 && (
                                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-200">
                                  <h4 className="text-sm font-medium text-gray-900 mb-3">Team Performance Overview</h4>
                                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                    <div className="text-center">
                                      <div className="text-2xl font-bold text-blue-600">
                                        {teamMembers.reduce((sum, member) => sum + member.taskCount, 0)}
                                      </div>
                                      <div className="text-xs text-gray-600">Total Tasks</div>
                                    </div>
                                    <div className="text-center">
                                      <div className="text-2xl font-bold text-green-600">
                                        {teamMembers.reduce((sum, member) => sum + member.completedTasks, 0)}
                                      </div>
                                      <div className="text-xs text-gray-600">Completed</div>
                                    </div>
                                    <div className="text-center">
                                      <div className="text-2xl font-bold text-purple-600">
                                        {Math.round(teamMembers.reduce((sum, member) => sum + member.completionRate, 0) / teamMembers.length) || 0}%
                                      </div>
                                      <div className="text-xs text-gray-600">Avg Completion</div>
                                    </div>
                                    <div className="text-center">
                                      <div className="text-2xl font-bold text-orange-600">
                                        {teamMembers.filter(member => member.completionRate >= 80).length}
                                      </div>
                                      <div className="text-xs text-gray-600">High Performers</div>
                                    </div>
                                  </div>
                                </div>
                              )}

                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {teamMembers.map((member) => (
                                  <div key={member.id} className="p-4 bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                                    <div className="flex items-start justify-between">
                                      <div className="flex items-center space-x-3 flex-1">
                                        <div className="w-12 h-12 bg-gradient-to-br from-primary-100 to-primary-200 rounded-full flex items-center justify-center">
                                          <span className="text-sm font-bold text-primary-700">
                                            {member.firstName[0]}{member.lastName[0]}
                                          </span>
                                        </div>
                                        <div className="flex-1">
                                          <p className="text-sm font-semibold text-gray-900">
                                            {member.firstName} {member.lastName}
                                          </p>
                                          <p className="text-xs text-gray-500 mb-2">{member.email}</p>

                                          {/* Task Statistics */}
                                          <div className="space-y-2">
                                            <div className="flex items-center justify-between text-xs">
                                              <span className="text-gray-600">Tasks Progress</span>
                                              <span className="font-medium text-gray-900">
                                                {member.completedTasks}/{member.taskCount}
                                              </span>
                                            </div>
                                            <div className="w-full bg-gray-200 rounded-full h-2">
                                              <div
                                                className={`h-2 rounded-full transition-all duration-300 ${
                                                  member.completionRate >= 80 ? 'bg-green-500' :
                                                  member.completionRate >= 60 ? 'bg-yellow-500' :
                                                  member.completionRate >= 40 ? 'bg-orange-500' : 'bg-red-500'
                                                }`}
                                                style={{ width: `${member.completionRate}%` }}
                                              />
                                            </div>
                                            <div className="flex items-center justify-between">
                                              <span className="text-xs text-gray-500">
                                                Completion Rate: {member.completionRate}%
                                              </span>
                                              <span className={`text-xs px-2 py-1 rounded-full ${
                                                member.completionRate >= 80 ? 'bg-green-100 text-green-800' :
                                                member.completionRate >= 60 ? 'bg-yellow-100 text-yellow-800' :
                                                member.completionRate >= 40 ? 'bg-orange-100 text-orange-800' : 'bg-red-100 text-red-800'
                                              }`}>
                                                {member.completionRate >= 80 ? 'Excellent' :
                                                 member.completionRate >= 60 ? 'Good' :
                                                 member.completionRate >= 40 ? 'Average' : 'Needs Attention'}
                                              </span>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                      <button
                                        onClick={() => {
                                          setMemberToRemove(member);
                                          setIsRemoveMemberConfirmOpen(true);
                                        }}
                                        className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                                        title="Remove Member"
                                      >
                                        <UserMinusIcon className="w-4 h-4" />
                                      </button>
                                    </div>
                                  </div>
                                ))}
                                {teamMembers.length === 0 && (
                                  <div className="col-span-2 text-center py-12">
                                    <UserGroupIcon className="mx-auto h-12 w-12 text-gray-400" />
                                    <h3 className="mt-2 text-sm font-medium text-gray-900">No team members</h3>
                                    <p className="mt-1 text-sm text-gray-500">Get started by adding members to your team.</p>
                                    <div className="mt-6">
                                      <button
                                        onClick={() => setIsAddMemberModalOpen(true)}
                                        className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700"
                                      >
                                        <UserPlusIcon className="w-4 h-4 mr-2" />
                                        Add First Member
                                      </button>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}

                          {activeTab === 'projects' && (
                            <div className="space-y-4">
                              <h3 className="text-lg font-medium text-gray-900">Team Projects ({teamProjects.length})</h3>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {teamProjects.map((project) => (
                                  <div key={project.id} className="p-4 bg-gray-50 rounded-lg">
                                    <div className="flex items-center space-x-3">
                                      <div
                                        className="w-3 h-3 rounded-full"
                                        style={{ backgroundColor: project.color }}
                                      />
                                      <div className="flex-1">
                                        <h4 className="text-sm font-medium text-gray-900">{project.name}</h4>
                                        {project.description && (
                                          <p className="text-xs text-gray-500 mt-1 line-clamp-2">{project.description}</p>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                ))}
                                {teamProjects.length === 0 && (
                                  <p className="text-sm text-gray-500 text-center py-8 col-span-2">No projects assigned</p>
                                )}
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
                  <UserGroupIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">Select a team</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Choose a team from the list to manage its members and tasks.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Task Creation Modal */}
        <TaskModal
          isOpen={isCreateTaskModalOpen}
          onClose={() => setIsCreateTaskModalOpen(false)}
          creatorId={user?.id}
          onSubmit={handleCreateTask}
          teams={selectedTeam ? [{
            id: selectedTeam.id,
            name: selectedTeam.name,
            color: selectedTeam.color,
            description: selectedTeam.description || '',
            managerId: selectedTeam.managerId,
            createdAt: selectedTeam.createdAt,
            updatedAt: selectedTeam.updatedAt,
            isActive: true,
            members: [],
            projects: [],
            manager: selectedTeam.manager
          } as Team] : []} // Create a proper Team object for the modal
          users={teamMembers as User[]}
          projects={teamProjects}
          isLoading={false}
          initialData={{
            teamId: selectedTeam?.id,
            assigneeIds: [],
          }}
        />

        {/* Task Detail Modal */}
        <UserTaskModal
          isOpen={isUserTaskModalOpen}
          onClose={() => {
            setIsUserTaskModalOpen(false);
            setSelectedTask(null);
          }}
          task={selectedTask}
          onTaskUpdate={handleTaskUpdate}
        />

        {/* Add Member Modal */}
        {isAddMemberModalOpen && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setIsAddMemberModalOpen(false)} />
            <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
              <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
              <div className="relative inline-block w-full max-w-md p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-lg z-10">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Add Team Members</h3>
                <div className="space-y-3 max-h-60 overflow-y-auto">
                  {availableUsers
                    .filter(user => !teamMembers.some(member => member.id === user.id))
                    .map((user) => (
                      <label key={user.id} className="flex items-center space-x-3 p-2 hover:bg-gray-50 rounded">
                        <input
                          type="checkbox"
                          checked={selectedMembers.includes(user.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedMembers(prev => [...prev, user.id]);
                            } else {
                              setSelectedMembers(prev => prev.filter(id => id !== user.id));
                            }
                          }}
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-900">
                            {user.firstName} {user.lastName}
                          </p>
                          <p className="text-xs text-gray-500">{user.email}</p>
                        </div>
                      </label>
                    ))}
                </div>
                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    onClick={() => {
                      setIsAddMemberModalOpen(false);
                      setSelectedMembers([]);
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAddMembers}
                    disabled={selectedMembers.length === 0}
                    className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 disabled:opacity-50"
                  >
                    Add Members
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Task Assignment Management Modal */}
        {isTaskAssignmentModalOpen && taskToManage && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setIsTaskAssignmentModalOpen(false)} />
            <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
              <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
              <div className="relative inline-block w-full max-w-md p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-lg z-10">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  Manage Task Assignments: {taskToManage.title}
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  Select team members to assign to this task:
                </p>
                <div className="space-y-3 max-h-60 overflow-y-auto">
                  {teamMembers.map((member) => (
                    <label key={member.id} className="flex items-center space-x-3 p-2 hover:bg-gray-50 rounded">
                      <input
                        type="checkbox"
                        checked={taskAssignees.includes(member.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setTaskAssignees(prev => [...prev, member.id]);
                          } else {
                            setTaskAssignees(prev => prev.filter(id => id !== member.id));
                          }
                        }}
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">
                          {member.firstName} {member.lastName}
                        </p>
                        <p className="text-xs text-gray-500">
                          {member.taskCount} tasks • {member.completedTasks} completed
                        </p>
                      </div>
                    </label>
                  ))}
                </div>
                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    onClick={() => {
                      setIsTaskAssignmentModalOpen(false);
                      setTaskToManage(null);
                      setTaskAssignees([]);
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleUpdateTaskAssignments}
                    className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700"
                  >
                    Update Assignments
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Task Edit Modal */}
        <TaskModal
          isOpen={isEditTaskModalOpen}
          onClose={() => {
            setIsEditTaskModalOpen(false);
            setEditingTask(null);
          }}
          onSubmit={handleUpdateTask}
          task={editingTask}
          teams={selectedTeam ? [{
            id: selectedTeam.id,
            name: selectedTeam.name,
            color: selectedTeam.color,
            description: selectedTeam.description || '',
            managerId: selectedTeam.managerId,
            createdAt: selectedTeam.createdAt,
            updatedAt: selectedTeam.updatedAt,
            isActive: true,
            members: [],
            projects: [],
            manager: selectedTeam.manager
          } as Team] : []} // Create a proper Team object for the modal
          users={teamMembers as User[]}
          projects={teamProjects}
          creatorId={user?.id}
          isLoading={isLoading}
          initialData={{
            teamId: selectedTeam?.id,
            assigneeIds: editingTask?.assignees?.map(a => a.user.id) || [],
          }}
        />

        {/* Delete Task Confirmation */}
        <ConfirmDialog
          isOpen={isDeleteTaskDialogOpen}
          onClose={() => {
            setIsDeleteTaskDialogOpen(false);
            setTaskToDelete(null);
          }}
          onConfirm={confirmDeleteTask}
          title="Delete Task"
          message={`Are you sure you want to delete "${taskToDelete?.title}"? This action cannot be undone.`}
          confirmText="Delete"
          cancelText="Cancel"
          type="danger"
        />

        {/* Remove Member Confirmation */}
        <ConfirmDialog
          isOpen={isRemoveMemberConfirmOpen}
          onClose={() => {
            setIsRemoveMemberConfirmOpen(false);
            setMemberToRemove(null);
          }}
          onConfirm={handleRemoveMember}
          title="Remove Team Member"
          message={`Are you sure you want to remove ${memberToRemove?.firstName} ${memberToRemove?.lastName} from the team?`}
          confirmText="Remove"
          cancelText="Cancel"
          type="danger"
        />
      </div>
    </Layout>
  );
};

export default Management;
